import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { zefGo } from '@zerops/zef/ngrx-router';
import { storeUserDataSuccess } from '@zerops/zerops/core/user-base';
import { filter, map } from 'rxjs/operators';
import { forgetAuthorizeAppRequest, readCarriedAuthorizeAppRequest } from './z3-handover.carry';
import { asQuery, AuthorizeAppRequest } from './z3-handover.util';

/**
 * Resuming a Zerops Code sign-in hand-over once the user is fully signed in.
 *
 * This lives in core, not beside the `/authorize-app` page, because the OAuth
 * path is a **full page reload**: the browser leaves for the provider and comes
 * back to `/github-auth` in a fresh app. A lazy page module's effects are not
 * registered at that point, so a resume that lived there would simply never
 * run. `ZcpPoolClaimBaseEffect` is in core for the same reason.
 *
 * `storeUserDataSuccess` is the point the session is complete and an
 * organization is resolved — the same signal the pool-claim drain waits for.
 * Consumed once; an abandoned request expires on its own instead of resuming
 * a second time.
 */
@Injectable()
export class Z3HandoverBaseEffect {

  #actions$ = inject(Actions);

  onFullyAuthedResume$ = createEffect(() => this.#actions$.pipe(
    ofType(storeUserDataSuccess),
    map(() => readCarriedAuthorizeAppRequest()),
    filter((request): request is AuthorizeAppRequest => !!request),
    map((request) => {
      forgetAuthorizeAppRequest();
      return zefGo([ '/authorize-app' ], asQuery(request));
    })
  ));

}
